import { useEffect, useId, useRef } from 'react'
import { useCallAudioRoute } from '../hooks/use-call-audio-route'
import type { CallAudioOutputDevice } from '../../../services/voice-call/native-audio-route'
import { isNativeApp } from '../../../utils/platform'

function RouteGlyph({
  kind,
  className = 'h-7 w-7',
}: {
  kind: CallAudioOutputDevice['kind']
  className?: string
}) {
  if (kind === 'speaker') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M11 5L6 9H2v6h4l5 4V5z" />
        <path
          d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    )
  }
  if (kind === 'earpiece') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.34 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C9.61 21 2 13.39 2 4c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
      </svg>
    )
  }
  if (kind === 'bluetooth' || kind === 'car') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M7 7l10 10-5 5V2l5 5L7 17" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  if (kind === 'wired') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M4 14v-3a8 8 0 0116 0v3" strokeLinecap="round" />
        <path d="M4 14a2 2 0 104 0v-2M16 14a2 2 0 104 0v-2" />
      </svg>
    )
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 5L6 9H2v6h4l5 4V5z" />
    </svg>
  )
}

type RouteState = ReturnType<typeof useCallAudioRoute>

function RoutePickerSheet({ route, titleId }: { route: RouteState; titleId: string }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const { setPickerOpen } = route

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setPickerOpen])

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      role="presentation"
      onClick={() => setPickerOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-t-2xl bg-slate-900 px-4 pb-[max(1rem,var(--safe-bottom))] pt-2 text-white shadow-2xl sm:max-w-sm sm:rounded-2xl sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/25 sm:hidden" aria-hidden />
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold">
            Play call audio on
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={() => setPickerOpen(false)}
            className="rounded-full px-3 py-1 text-sm text-white/80 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            Close
          </button>
        </div>
        <ul className="flex flex-col gap-1">
          {route.options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                onClick={() => void route.select(option)}
                aria-current={option.selected ? 'true' : undefined}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                  option.selected ? 'bg-emerald-400/20 text-emerald-100' : 'hover:bg-white/10'
                }`}
                aria-label={
                  option.selected
                    ? `${option.name}, on`
                    : `Play call audio on ${option.name}`
                }
              >
                <span className="flex min-w-0 items-center gap-3">
                  <RouteGlyph kind={option.kind} className="h-5 w-5 shrink-0" />
                  <span className="truncate font-medium">{option.name}</span>
                </span>
                {option.selected && (
                  <span className="shrink-0 text-sm font-medium text-emerald-300">On</span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {route.options.length === 0 && (
          <p className="px-1 py-3 text-sm text-white/70">No other audio outputs available.</p>
        )}
        {isNativeApp() && (
          <button
            type="button"
            onClick={() => void route.openSystemPicker()}
            className="mt-2 w-full rounded-xl px-3 py-3 text-left text-sm font-medium text-white/85 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            More outputs…
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * In-call audio-route control.
 *
 * `variant="rack"` (default) sits in the call-control grid:
 *  - Builtins only → a plain Speaker toggle (tap flips speaker ⇄ earpiece),
 *    highlighted while the loud speaker is live. No picker ceremony for the
 *    99% case.
 *  - An external device (earbuds / car / wired) is attached → the button shows
 *    the live output by name and opens the route picker (bottom sheet).
 *
 * `variant="pill"` is the passive indicator for surfaces without a control
 * rack (e.g. the incoming-ring screen): renders NOTHING while audio is on the
 * expected builtin route, and a compact pill naming the device only when the
 * audio is somewhere non-obvious. Tap opens the same picker.
 */
export function CallAudioRouteControl({
  active,
  compact = false,
  variant = 'rack',
}: {
  active: boolean
  compact?: boolean
  variant?: 'rack' | 'pill'
}) {
  const route = useCallAudioRoute(active)
  const titleId = useId()

  if (!route.supported) return null

  const picker = route.pickerOpen && <RoutePickerSheet route={route} titleId={titleId} />

  if (variant === 'pill') {
    if (!route.externalActive) return null
    return (
      <>
        <button
          type="button"
          onClick={() => route.setPickerOpen(true)}
          aria-label={`Call audio is playing on ${route.label}. Change audio output`}
          aria-haspopup="dialog"
          aria-expanded={route.pickerOpen}
          data-tour="call-audio-route"
          className="inline-flex max-w-56 items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <RouteGlyph kind={route.kind} className="h-4 w-4 shrink-0" />
          <span className="truncate">{route.label}</span>
        </button>
        {picker}
      </>
    )
  }

  // Rack variant. Native with no external device: a plain speaker toggle.
  const builtinToggle = isNativeApp() && !route.externalAvailable
  const highlighted = route.speakerOn && !route.externalActive

  return (
    <>
      <div className={compact ? '' : 'flex w-20 flex-col items-center gap-2'}>
        <button
          type="button"
          onClick={() =>
            builtinToggle ? void route.toggleBuiltin() : route.setPickerOpen(true)
          }
          aria-label={
            builtinToggle
              ? `Speaker ${route.speakerOn ? 'on' : 'off'}`
              : `Audio output: ${route.label}. Change audio output`
          }
          aria-pressed={builtinToggle ? route.speakerOn : undefined}
          aria-haspopup={builtinToggle ? undefined : 'dialog'}
          aria-expanded={builtinToggle ? undefined : route.pickerOpen}
          data-tour="call-audio-route"
          title={route.label}
          className={`relative flex ${compact ? 'h-14 w-14' : 'h-16 w-16'} items-center justify-center rounded-full shadow-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
            highlighted
              ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80 hover:bg-emerald-300'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          <RouteGlyph kind={builtinToggle ? 'speaker' : route.kind} />
        </button>
        {!compact && (
          <span className="max-w-[5.5rem] truncate text-center text-xs font-medium leading-tight text-white/85">
            {builtinToggle ? 'Speaker' : route.label}
          </span>
        )}
      </div>
      {picker}
    </>
  )
}
